import ApplicationController from "./application_controller";
import Tribute from "tributejs";
import Trix from "trix";

export default class extends ApplicationController {
  static targets = [ "field" ]

  connect() {
    // Verhindere Doppel-Initialisierung
    if (this._isConnecting || this._isConnected) {
      return
    }
    this._isConnecting = true
    
    // Finde das trix-editor Element
    // rich_text_area erstellt ein verstecktes Input-Feld, das trix-editor Element wird daneben platziert
    let trixEditor = this.fieldTarget
    
    // Wenn fieldTarget bereits ein trix-editor ist, verwende es direkt
    if (trixEditor.tagName === 'TRIX-EDITOR') {
      this.initializeWithEditor(trixEditor)
      this._isConnected = true
      this._isConnecting = false
      return
    }
    
    // Wenn fieldTarget das versteckte Input ist, finde das zugehörige trix-editor Element
    const findTrixEditor = () => {
      // Das trix-editor Element hat ein input Property, das auf das Input-Feld verweist
      const inputId = trixEditor.id
      const inputName = trixEditor.name
      
      // Suche nach trix-editor mit entsprechendem input Property
      const allTrixEditors = document.querySelectorAll('trix-editor')
      
      for (let editor of allTrixEditors) {
        if (editor.input && (editor.input.id === inputId || editor.input.name === inputName)) {
          return editor
        }
      }
      
      // Fallback: Suche im nächsten Geschwister-Element oder im Parent
      const wrapper = this.fieldTarget.closest('.trix-wrapper') || this.fieldTarget.parentElement
      if (wrapper) {
        const foundEditor = wrapper.querySelector('trix-editor')
        if (foundEditor) {
          return foundEditor
        }
      }
      
      return null
    }
    
    trixEditor = findTrixEditor()
    
    if (!trixEditor || trixEditor.tagName !== 'TRIX-EDITOR') {
      // Wenn das trix-editor noch nicht existiert (z.B. im Slideover), warte auf trix-initialize
      const inputElement = this.fieldTarget
      const inputId = inputElement.id
      const inputName = inputElement.name
      
      this.initializeListener = (event) => {
        // Prüfe, ob das Event für unser Input-Feld ist
        const editor = findTrixEditor()
        if (editor && editor.input && (editor.input.id === inputId || editor.input.name === inputName)) {
          this.initializeWithEditor(editor)
          // Entferne den Listener nach erfolgreicher Initialisierung
          document.removeEventListener('trix-initialize', this.initializeListener)
          this.initializeListener = null
          this._isConnected = true
          this._isConnecting = false
        }
      }
      
      // Warte auf trix-initialize Event (wird ausgelöst, wenn Trix initialisiert wird)
      document.addEventListener('trix-initialize', this.initializeListener)
      
      // Fallback: Versuche es mehrfach nach kurzer Verzögerung (für dynamisch geladene Formulare)
      let attempts = 0
      const maxAttempts = 10
      const checkInterval = setInterval(() => {
        attempts++
        const editor = findTrixEditor()
        if (editor) {
          this.initializeWithEditor(editor)
          if (this.initializeListener) {
            document.removeEventListener('trix-initialize', this.initializeListener)
            this.initializeListener = null
          }
          this._isConnected = true
          this._isConnecting = false
          clearInterval(checkInterval)
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval)
          if (this.initializeListener) {
            document.removeEventListener('trix-initialize', this.initializeListener)
            this.initializeListener = null
          }
          this._isConnecting = false
        }
      }, 100)
      
      this._isConnected = true
      this._isConnecting = false
      return
    }
    
    this.initializeWithEditor(trixEditor)
    this._isConnected = true
    this._isConnecting = false
  }
  
  initializeWithEditor(trixEditor) {
    // Wenn bereits initialisiert, cleanup und neu initialisieren
    if (this.tribute) {
      const editableElement = this.trixEditor?.querySelector('[contenteditable]') || this.trixEditor
      if (editableElement && this.replacedHandler) {
        editableElement.removeEventListener("tribute-replaced", this.replacedHandler)
      }
      try {
        if (editableElement) {
          this.tribute.detach(editableElement)
        }
      } catch (e) {
        // Silent fail
      }
      this.tribute = null
    }
    
    // Warte bis der Trix Editor vollständig geladen ist
    if (!trixEditor.editor) {
      const initHandler = () => {
        this.trixEditor = trixEditor
        this.editor = trixEditor.editor
        this.initializeTribute()
      }
      trixEditor.addEventListener('trix-initialize', initHandler, { once: true })
      
      // Fallback: Wenn trix-initialize nicht ausgelöst wird, versuche es nach kurzer Verzögerung
      setTimeout(() => {
        if (!this.trixEditor && trixEditor.editor) {
          initHandler()
        }
      }, 500)
    } else {
      this.trixEditor = trixEditor
      this.editor = trixEditor.editor
      this.initializeTribute()
    }
  }

  disconnect() {
    if (this.tribute && this.trixEditor) {
      // Finde das editierbare Element, an das Tribute angehängt wurde
      const editableElement = this.trixEditor.querySelector('[contenteditable]') || this.trixEditor
      
      // Entferne Event-Listener
      if (this.replacedHandler && editableElement) {
        editableElement.removeEventListener("tribute-replaced", this.replacedHandler)
        this.replacedHandler = null
      }
      
      try {
        if (editableElement) {
          this.tribute.detach(editableElement)
        }
      } catch (e) {
        // Silent fail
      }
      
      // Cleanup
      this.tribute = null
      this.trixEditor = null
      this.editor = null
    }
    // Entferne Event-Listener falls vorhanden
    if (this.initializeListener) {
      document.removeEventListener('trix-initialize', this.initializeListener)
      this.initializeListener = null
    }
    
    // Reset connection flags
    this._isConnected = false
    this._isConnecting = false
  }

  initializeTribute() {
    // Finde das editierbare Element innerhalb des trix-editor
    // Bei Trix ist das normalerweise ein div mit contenteditable
    let editableElement = this.trixEditor.querySelector('[contenteditable=""]') || 
                          this.trixEditor.querySelector('[contenteditable="true"]')
    
    // Fallback: Wenn kein contenteditable gefunden wurde, verwende trix-editor selbst
    if (!editableElement) {
      editableElement = this.trixEditor
    }
    
    // Finde den Container für das Dropdown (Modal/Slideover oder body)
    const menuContainerElement = editableElement.closest('.tmodal, [data-controller*="tmodal"], [data-controller*="slideover"], .fixed.inset-0')
    const menuContainer = menuContainerElement || document.body
    
    this.tribute = new Tribute({
      allowSpaces: true,
      trigger: '@',
      menuContainer: menuContainer,
      positionMenu: true,
      replaceTextSuffix: '',
      requireLeadingSpace: false,
      noMatchTemplate: '<li class="tribute-item-no-match">Keine Übereinstimmung gefunden</li>',
      selectTemplate: function(item) {
        if (item && item.original) {
          // Gib nur den Namen/Email zurück, ohne @
          return (item.original.type === 'team' ? item.original.name : item.original.email)
        }
        return ''
      },
      lookup: function(item) {
        // Teams kennzeichnen
        if (item.type === 'team') {
          return '👥 ' + item.name + ' (Team)'
        }
        // Zeige nur E-Mail wenn kein Name vorhanden ist
        if (!item.firstname || !item.lastname || item.firstname === 'null' || item.lastname === 'null') {
          return item.email
        }
        return item.firstname + ' ' + item.lastname + ' (' + item.email + ')'
      },
      values: this.fetchUsers,
    })
    
    this.tribute.attach(editableElement)
    this.tribute.range.pasteHtml = this._pasteHtml.bind(this)
    
    // Entferne vorherigen Event-Listener falls vorhanden
    if (this.replacedHandler) {
      editableElement.removeEventListener("tribute-replaced", this.replacedHandler)
    }
    
    // Speichere Handler-Referenz für späteren Cleanup
    this.replacedHandler = this.replaced.bind(this)
    editableElement.addEventListener("tribute-replaced", this.replacedHandler)
  }

  fetchUsers(text, callback) {
    // Wenn nur "@" getippt wurde (text ist leer), hole zuletzt erwähnte Benutzer vom Backend
    if (!text || text.trim() === '') {
      fetch(`/backend/mentions.json?recent=true`)
        .then(response => response.json())
        .then(users => {
          if (users && users.length > 0) {
            callback(users)
          } else {
            // Wenn keine zuletzt erwähnten Benutzer vorhanden sind, zeige alle (leere Suche)
            fetch(`/backend/mentions.json?query=`)
              .then(response => response.json())
              .then(allUsers => callback(allUsers))
              .catch(error => callback([]))
          }
        })
        .catch(error => {
          // Fallback: normale Suche mit leerem Query
          fetch(`/backend/mentions.json?query=`)
            .then(response => response.json())
            .then(users => callback(users))
            .catch(err => callback([]))
        })
      return
    }
    
    // Wenn getippt wurde, führe normale Suche durch
    fetch(`/backend/mentions.json?query=${encodeURIComponent(text)}`)
        .then(response => response.json())
        .then(users => callback(users))
        .catch(error => callback([]))
  }

  replaced(e) {
    let mention = e.detail.item.original
    
    let attachment = new Trix.Attachment({
      sgid: mention.sgid,
      content: mention.type === 'team' ? mention.name : mention.email
    })
    
    // Füge Attachment ein
    this.editor.insertAttachment(attachment)
    this.editor.insertString(" ")
  }

  _pasteHtml(html, startPos, endPos) {
    let position = this.editor.getPosition()
    this.editor.setSelectedRange([position - endPos, position])
    this.editor.deleteInDirection("backward")
  }
}